import React, { useState, useRef } from 'react';

// --- SVG ICONS ---
const CloseIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const PaperclipIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"></path>
  </svg>
);

const DownloadIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="7 10 12 15 17 10"></polyline>
    <line x1="12" y1="15" x2="12" y2="3"></line>
  </svg>
);

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'numeric',
    year: 'numeric'
  });
};

// --- MAIN VIEW MODAL COMPONENT ---
const MaterialReceiveViewModal = ({ open, onClose, data }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  const API_URL = "/api";

  // Refs for Resizing (Keeping same functionality as Edit Form)
  const [columnWidths, setColumnWidths] = useState({
    lineNo: 80, productCode: 180, taskNo: 100, desc: 280,
    siteQty: 200, issuedQty: 200, qtyToReceive: 200, uom: 80, action: 50,remark:280,activity: 200,      // ADD THIS
    subActivity: 200 
  });
  const resizingRef = useRef(null);

  const startResize = (columnKey, startX) => {
    resizingRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    setColumnWidths(prev => ({ ...prev, [columnKey]: Math.max(60, startWidth + delta) }));
  };

  const stopResize = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
    resizingRef.current = null;
  };

  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width }}
      className={`relative p-2 ${align === 'right' ? 'text-right' : ''} select-none`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-blue-400"
        onMouseDown={(e) => onResize(e.clientX)}
      />
    </th>
  );

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

   const handleDownloadHeaderAttachment = async () => {
    try {
      if (!header.ReceiveHeaderID) return;
      const token = localStorage.getItem('authToken');
      
      const response = await fetch(`${API_URL}/material-receive/${header.ReceiveHeaderID}/header/file`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = header.AttachmentFileName || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error(error);
      alert("Error downloading file");
    }
  };

  if (!open) return null;
  if (!data || !data.header) return null;

  const { header, lines } = data;

  // Calculation for summary section
  const totalQty = lines ? lines.reduce((acc, line) => acc + (parseFloat(line.QtyToReceive) || 0), 0) : 0;

  return (
    <>
      <div className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        <style>
          {`
            .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            .hide-scrollbar::-webkit-scrollbar { display: none; }
            .custom-scrollbar::-webkit-scrollbar { height: 8px; }
            .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
            .resizable-table {
              table-layout: fixed;
              min-width: max-content;
            }
          `}
        </style>
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
        
        <div className={`bg-white shadow-2xl w-full flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        } ${ isMaximized ? 'h-full max-w-full' : 'h-full max-w-6xl' }`} tabIndex={0}>
          
          {/* --- MODAL HEADER --- */}
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                View Material Receive
              </h2>
              <div className="flex items-center gap-2">
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={toggleMaximize}>
                  {isMaximized ? <MinimizeIcon size={20} /> : <MaximizeIcon size={20} />}
                </button>
                <button className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1" onClick={onClose}>
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>

          {/* --- FORM BODY (READ ONLY) --- */}
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <div className="space-y-6 pb-6">
              
              {/* --- HEADER SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">Material Receive Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                  {/* Receive No */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receive No</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 font-medium">
                      {header.ReceiveHeaderNo || '-'}
                    </div>
                  </div>

                  {/* Material Issue OR Material Return - DYNAMIC FIELD */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      {header.ReceiveSource === 'Return' ? 'Material Return' : 'Material Issue'}
                    </label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700 truncate">
                      {header.ReceiveSource === 'Return' 
                        ? (header.ReceiveSourceNo || '-') 
                        : (header.IssueHeaderNo || '-')
                      }
                    </div>
                  </div>

                  {/* Site Indent */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Site Indent</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.SiteIndentNo || '-'}
                    </div>
                  </div>

                  {/* Survey BOQ */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Survey BOQ</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.SurveyBOQNo || '-'}
                    </div>
                  </div>

                  {/* Receive Date */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receive Date</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {formatDate(header.ReceiveDate) }
                    </div>
                  </div>
                  
{/* Approval/BC Status - ADD THIS NEW FIELD */}
<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]"> Status</label>
  <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 shadow-sm">
    {(header.PostingSiteIndent === 1 || header.PostingSiteIndent === true) ? (
      <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
        Material Received Successfully
      </span>
    ) : (header.SendToBC === 1 || header.SendToBC === true || header.SendToBc === 1 || header.SendToBc === true) ? (
      <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
        Sent to BC (Ready to Post)
      </span>
    ) : header.ApprovalStatus === 'PENDING' ? (
      <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
        Pending Approval (Level {header.CurrentLevel || 1})
      </span>
    ) : header.ApprovalStatus === 'REJECTED' ? (
      <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
        Rejected
      </span>
    ) : header.ApprovalStatus === 'APPROVED' ? (
      <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
        Approved (Ready to Send BC)
      </span>
    ) : (
      <span className="text-xs text-gray-500 italic">Open</span>
    )}
  </div>
</div>


                  {/* Vendor */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Vendor</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.VendorCode || '-'} - {header.VendorName || '-'}
                    </div>
                  </div>

                  {/* Project */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Project</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.ProjectNo || '-'} - {header.ProjectDescription || '-'}
                    </div>
                  </div>

                  {/* From Feeder */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">From Feeder</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.FromFeederCode } - {header.FromFeederDescription} 
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Activity</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.ActivityCode } - {header.ActivityDesc} 
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Activity</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.SubActivityCode } - {header.SubActivityDesc} 
                    </div>
                  </div>

                  {/* To Feeder */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">To Feeder</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.ToFeederCode } -{header.ToFeederDescription}
                    </div>
                  </div>

                  {/* Circle */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Circle</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase">
                      {header.Circle || '-'}
                    </div>
                  </div>

                  {/* District */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">District</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase">
                      {header.District || '-'}
                    </div>
                  </div>

                  {/* Division */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Division</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase">
                      {header.Division || '-'}
                    </div>
                  </div>

                  {/* Sub Division */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Division</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase">
                      {header.SubDivision || '-'}
                    </div>
                  </div>

                  {/* Sub Station */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Sub Station</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-500 uppercase">
                      {header.SubStation || '-'}
                    </div>
                  </div>

                  {/* Receiver Name */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">Receiver Name</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700">
                      {header.ReceiverName || '-'}
                    </div>
                  </div>

                   <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Attachment</label>
                    <div className="flex items-center gap-3 border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 min-h-[42px]">
                      <PaperclipIcon size={18} className="text-gray-400" />
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {header.AttachmentFileName || 'No attachment uploaded'}
                      </span>
                      {header.AttachmentFileName && (
                        <button
                          type="button"
                          onClick={handleDownloadHeaderAttachment}
                          className="text-blue-600 hover:text-blue-800 p-1.5 rounded-full hover:bg-blue-50 transition-colors"
                          title="Download Attachment"
                        >
                          <DownloadIcon size={18} />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="flex flex-col gap-2 md:col-span-2 lg:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">Remarks</label>
                    <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-gray-50 text-gray-700 min-h-[60px] whitespace-pre-wrap">
                      {header.Remarks || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* --- LINES SECTION --- */}
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">Receive Lines</h3>
                </div>
                
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.lineNo} onResize={(x) => startResize('lineNo', x)} align="center">Line No</ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>Product Code</ResizableTh>
                        <ResizableTh width={columnWidths.taskNo} onResize={(x) => startResize('taskNo', x)}>Task No</ResizableTh>
                        <ResizableTh width={columnWidths.desc} onResize={(x) => startResize('desc', x)}>Description</ResizableTh>
                        <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                        <ResizableTh width={columnWidths.activity} onResize={(x) => startResize('activity', x)}>
                          Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.subActivity} onResize={(x) => startResize('subActivity', x)}>
                          Sub Activity
                        </ResizableTh>
                        <ResizableTh width={columnWidths.siteQty} onResize={(x) => startResize('siteQty', x)} align="right">Indent Qty</ResizableTh>
                        <ResizableTh width={columnWidths.issuedQty} onResize={(x) => startResize('issuedQty', x)} align="right">Issued Qty</ResizableTh>
                        <ResizableTh width={columnWidths.qtyToReceive} onResize={(x) => startResize('qtyToReceive', x)} align="right">Qty To Receive</ResizableTh>
                         <ResizableTh width={columnWidths.remark} onResize={(x) => startResize('remark', x)} align="right">Remark</ResizableTh>
                        
                        {/* Keeping action column for layout consistency but removing the button */}
                        <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center"></ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {lines && lines.length > 0 ? lines.map((line, idx) => (
                        <tr key={idx} className="border-t border-[#E2E8F0]">
                          <td className="p-2 text-center" style={{width: columnWidths.lineNo}}>
                            <span className="block w-full text-center">{line.IssueLineNumber}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.productCode}}>
                            <span className="block w-full uppercase">{line.ProductCode}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.taskNo}}>
                            <span className="block w-full">{line.TaskNo}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.desc}}>
                            <span className="block w-full">{line.Description}</span>
                          </td>
                            <td className="p-2" style={{width: columnWidths.uom}}>
                            <span className="block w-full uppercase">{line.UnitOfMeasure}</span>
                          </td>
                           <td className="p-2" style={{width: columnWidths.activity}}>
                            <span className="block w-full text-sm">
                              {line.ActivityCode && line.ActivityDescription 
                                ? `${line.ActivityCode} - ${line.ActivityDescription}` 
                                : '-'}
                            </span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.subActivity}}>
                            <span className="block w-full text-sm">
                              {line.SubActivityCode && line.SubActivityDescription 
                                ? `${line.SubActivityCode} - ${line.SubActivityDescription}` 
                                : '-'}
                            </span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.siteQty}}>
                            <span className="block w-full text-right text-gray-500">{parseFloat(line.SiteIndentQty || 0).toFixed(6)}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.issuedQty}}>
                            <span className="block w-full text-right text-gray-500">{parseFloat(line.IssuedQty || 0).toFixed(6)}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.qtyToReceive}}>
                            <span className="block w-full text-right font-medium">{parseFloat(line.QtyToReceive || 0).toFixed(6)}</span>
                          </td>
                          <td className="p-2" style={{width: columnWidths.remark}}>
                            <span className="block w-full text-right font-medium">{line.Remark}</span>
                          </td>
                          <td className="p-2 text-center" style={{width: columnWidths.action}}>
                            {/* Empty in View Mode */}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="9" className="p-4 text-center text-gray-500">No lines available</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Lines</span>
                      <span className="text-base font-semibold">{lines ? lines.length : 0}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-sm text-gray-500">Total Qty To Receive</span>
                      <span className="text-base font-semibold text-lg text-blue-600">
                        {totalQty.toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* --- FOOTER --- */}
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button 
                type="button" 
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default MaterialReceiveViewModal;